<template lang="html">
  <div class="bim-table">
    <table class="v__table">
      <colgroup>
        <col
          v-for="(tableColumn,index) in columns"
          :key="`colgroup_${index}`"
          :width="tableColumn.width || `${100 / columns.length}%`"
        >
      </colgroup>
      <thead>
        <tr>
          <th
            v-for="(tableColumn,index) in columns"
            :key="`th_${index}`"
            @click="tableColumn.sortable ? sortableClicked(index) : () => {}"
          >
            <div :class="{ sortable: tableColumn.sortable }">
              <div :class="{ sortable__label: tableColumn.sortable }">
                <template v-if="typeof tableColumn.label === 'string'">
                  {{ tableColumn.label }}
                </template>
                <component
                  v-else-if="typeof tableColumn.label === 'function'"
                  :is="tableColumn.label()"
                  :item="tableColumn"
                />
              </div>
              <div
                v-if="tableColumn.sortable"
                class="sortable__icon ml-4"
                v-html="require('@/assets/img/icon-filter-upper-small.svg')"
                :class="{
                  active: sortFieldIndex === index,
                  up: sortDirection === SortDirection.Up && sortFieldIndex === index
                }"
              ></div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          class="v__row"
          :class="rowClass(rowIndex, row)"
          v-for="(row, rowIndex) in paginatedRows"
          :key="`tr_${rowIndex}`"
          @click="onRowClicked({ row, rowIndex, $event })"
        >
          <td
            v-for="(tableColumn, cellIndex) in columns"
            :key="`col-${rowIndex}-${cellIndex}`"
            :class="columnClass(tableColumn)"
            :colspan="row.colspans && row.colspans[cellIndex]"
            v-show="getColumnValue(tableColumn, row) || tableColumn.icons || !row.colspans"
            >
              <!-- @slot Слот для кастомизации вывода (для включения, в объекте колонок, надо передать slotKey с наименованием слота) -->
              <slot :name="tableColumn.slotKey ? `item.${tableColumn.slotKey}` : `item.${rowIndex}.${cellIndex}`" :item="row">
                <span v-if="tableColumn.icons && tableColumn.icons.length" class="d-flex align-center justify-end">
                  <span
                    class="action__icon flex-auto"
                    v-for="(icon,iconIndex) in tableColumn.icons"
                    :key="iconIndex"
                    :center="icon.center"
                    :title="tableColumn.icons[iconIndex].title"
                    @click="iconClicked($event, icon, row, rowIndex)"
                    v-html="iconUrl(icon, row)"
                  ></span>
                </span>
                <span v-else-if="tableColumn.component">
                  <component
                    :is="tableColumn.component.el"
                    v-bind="tableColumn.component.props"
                    v-on="tableColumn.component.listeners"
                    :key="`component_${rowIndex}_${cellIndex}`"
                    :item="row"
                  />
                </span>
                <span v-else-if="isStringColumn(tableColumn, row)">
                  {{ getColumnValue(tableColumn, row) }}
                </span>
                <span v-else>
                  <component
                    :key="`component_${rowIndex}_${cellIndex}`"
                    :is="getColumnValue(tableColumn, row)"
                    :item="row"
                    :data="tableColumn.data ? tableColumn.data : null"
                  />
                </span>
              </slot>
            </td>
        </tr>
      </tbody>
      <tfoot v-if="Object.keys(footer).length">
        <tr class="v__row">
          <td
            v-for="(item, key) in footer"
            :key="`col-footer-${key}`"
            :class="`col-footer col-footer-${key}`"
          >
            <!-- @slot Слот для кастомизации вывода колонок у футера  -->
            <slot :name="`footer.${key}`" :footer="footer">{{ item }}</slot>
          </td>
        </tr>
      </tfoot>
    </table>
    <div class="d-flex mt-9" v-if="totalPages && activePage">
      <bim-pagination
        v-if="totalPages && activePage"
        :totalPages="totalPages"
        :activePage="activePage"
        @changePage="onChangePage"
        class="mr-auto flex-grow-1"
        disableInnerMargin
      />
      <bimSelect
        width=75px
        v-if="configureItemsOnPage"
        class="bim-table__items-on-page ml-9"
        :value="itemsOnPage"
        @change="v => $emit('changeItemsOnPage', v)"
        :options="configureItemsOnPage.map(value => ({ label: value, value }))"
        placeholder
        listPosition=top
      />
    </div>
  </div>
</template>

<script>
  import bimPagination from '@/components/ui/bimPagination/bimPagination'
  import bimSelect from '@/components/form/bimSelect/bimSelect'
  import { copyToast } from '@/utils/clipboard'
  import { sortByString, sort } from '@/utils/helpers'

  const SortDirection = {
    Off: 0,
    Down: 1,
    Up: 2
  }

  export default {
    name: 'bimTable',
    components: {
      'bim-pagination': bimPagination,
      bimSelect
    },
    props: {
      /**
       * Массив объектов со всеми колонками. Может содержать следующие поля: <br /><br />
       * <b>label</b> - заголовок колонки (string); <br />
       * <b>field</b> - функция, возвращающая имя строки из пропа rows Например: field: row => row[имя_поля]. Так же принимает в себя целый компонент, у которого в пропсе будет элемент item с объектом строки; <br />
       * <b>width</b> - ширина колонки в любых единцах. Например: 100px или 40%; <br />
       * <b>align</b> - выравнивание колонки (left, right, center); <br />
       * <b>icons</b> - массив иконок. Например: [ { type: row => 'edit', clickable: true } ] <br />
       * <b>copy</b> - Boolean, при клике на колонку будет копироваться значение <br />
       * <b>sortable</b> - Boolean или своя функция сортировки, возможность сортировать колонку <br />
       * <b>sortValue</b> - функция, возвращающая имя строки из пропа rows Например: field: row => row[имя_поля] (нужно для сортирвки) <br />
       * <b>data</b> - объект для дополнительных данных (для отрисовки значения в виде компонента)
       * <b>colspans</b> - массив значений для каждой колонки([2, 0, 1, 1] -> |__|_|_|)
       * <b>slotKey</b> - наименование слота
       */
      columns: {
        type: Array,
        default: () => ([]),
        prop: {
          label: String,
          field: Function, // функция как рисовать поле
          width: String, // ширина колонки
          data: Object, // объект для дополнительных данных
          align: String, // выравнивание колонки (left, right, center)
          sortValue: Function, // функция (как field - для получения значения) для сортирвки
          sortable: Boolean | Function, // возможность сортировать колонку
          icons: [], // массив иконок { type: row => 'edit', clickable: true }
          slotKey: String // наименование слота
        }
      },
      /**
       * Объект с полями строки. Может быть любое количество ключей. Выборка по строкам идёт в функции field пропса columns. <br /><br />
       * Например: rows: [{ name: 'Продукт 1', symbol: 'pc-24-G' }] - одна строка <br />
       * В пропсе columns в первой колонке в объекте заполняем поле field: row => row.name и таким образом в первой колонке будет выборка по полю name
       */
      rows: { // элементы списка
        type: Array,
        default: () => ([]),
        prop: {
          id: String,
          name: String,
          // ... любые другие поля элемента
        }
      },
      /**
       * Объект для вывода футера.
       */
      footer: {
        type: Object,
        default: () => ({})
      },
      /**
       * Подсвечивает нужную строку. Принимает индекс строки, начинающийся с 0.
       */
      activeRowIndex: { // выбранный элемент списка
        type: Number
      },
      /**
       * Есть возможность кликнуть по строке
       */
      makeRowClicked: {
        type: Boolean,
        default: true
      },
      /**
       * Текущая страница пагинации
       */
      activePage: {
        type: Number,
        default: 1
      },
      /**
       * Общее количество страниц в пагинации
       */
      totalPages: {
        type: Number
      },
      /**
       * Число элементов на странице
       */
      itemsOnPage: {
        type: [Number, String]
      },
      /**
       * Отображать настройку кол-ва строк<br/>
       * принимает массив с возможными значениями
       */
      configureItemsOnPage: {
        type: Array,
        default: null
      }
    },
    data () {
      return {
        SortDirection,
        sortDirection: SortDirection.Off, // направление сортировки -  1-down, 2-up, 0-off
        sortFieldIndex: null // колонка по которой необходимо сортировать
      }
    },
    computed: {
      paginatedRows () {
        const count = +this.itemsOnPage
        if (isNaN(count)) return this.sortableRows

        const start = (this.activePage - 1) * count
        return this.sortableRows.slice(start, start + count)
      },

      // отсортированные строки
      sortableRows () {
        if (this.sortFieldIndex === null || !this.rows.length) {
          return this.rows
        }

        const tableColumn = this.columns[this.sortFieldIndex]

        if (typeof tableColumn.sortable === 'function') {
          return tableColumn.sortable(this.rows, this.sortDirection === SortDirection.Down)
        }

        const firstValue = this.getColumnValue(tableColumn, this.rows[0], true)
        const isNumber = typeof parseFloat(firstValue) === 'number'

        if (!~['string', 'number'].indexOf(typeof firstValue)) {
          return this.rows
        }

        // сортируем список
        const sortRows = (rows, isNumber) => {

          if (isNumber) {
            return sort(rows, 'sortValue', this.sortDirection === SortDirection.Down)
          }

          return sortByString(rows, 'sortValue', this.sortDirection === SortDirection.Down)
        }

        // поиск дочерних элементов
        const findChildren = (id, isNumber) => {
          const children = this.rows.filter(row => `${row.sortParentId}` === `${id}`)

          const resChildren = children.map(child => {
            const sortValueChild = this.getColumnValue(tableColumn, child, true)

            return {
              ...child,
              sortValue: isNumber ? +sortValueChild : sortValueChild
            }
          })

          return sortRows(resChildren, isNumber)
        }

        let rows = this.rows.filter(row => !row.sortParentId)

        rows = rows.map(row => {
          const sortValueChild = this.getColumnValue(tableColumn, row, true)

          return {
            ...row,
            sortValue: isNumber ? +sortValueChild : sortValueChild
          }
        })

        rows = sortRows(rows, isNumber)

        const result = []
        rows.forEach(row => {
          const sortValue = this.getColumnValue(tableColumn, row, true)
          row.sortValue = isNumber ? +sortValue : sortValue

          result.push(row)

          // поиск дочерних элементов
          if (row.id) {
            const children = findChildren(row.id, isNumber)

            children.forEach(child => result.push(child))
          }
        })

        return result
      }
    },
    methods: {

      rowClass (rowIndex, row) {
        const css = {
          active__row: this.activeRowIndex === rowIndex,
          hover__row: this.makeRowClicked || row.makeRowClicked
        }

        if (row.cssClass) {
          css[row.cssClass] = true
        }

        return css
      },

      onChangePage (page) {
        /**
         * Возвращает текущую страницу пагинации
         * @param {number} page - текущая страница
         */
        this.$emit('changePage', page)
      },

      columnClass (tableColumn) {
        const cssClass = {
          col__right: tableColumn.align === 'right',
          col__center: tableColumn.align === 'center'
        }

        if (tableColumn.class) {
          cssClass[tableColumn.class] = true
        }

        return cssClass
      },

      formId (row) {
        // eslint-disable-next-line
        return JSON.stringify(row).replace(/(\,|"|:| )/g, '')
      },

      isStringColumn (tableColumn, row) {
        const value = typeof this.getColumnValue(tableColumn, row)

        return ~['string', 'number'].indexOf(value)
      },

      getColumnValue (column, row, sort = false) {
        const { field, sortValue } = column

        if (sort && typeof sortValue === 'function') {
          return sortValue(row)
        }

        if (typeof field === 'undefined') {
          return ''
        }

        if (typeof field === 'function') {
          return field(row)
        }

        // тут может быть как объект так и строка и число
        return field
      },

      onRowClicked (payload) {

        const td = payload.$event.target.closest('td')
        if (this.columns[td.cellIndex].copy) {
          return copyToast(this.$toast, td.innerText)
        }

        if (!this.makeRowClicked && !payload.row.makeRowClicked) {
          return
        }

        /**
         * Возвращает объект с информацией о строке при клике. Какие поля входят в объект смотри в properties ==>
         * @param {object} row - объект строки со всеми полями
         * @param {number} rowIndex - индекс строки
         */
        this.$emit('rowClicked', payload)
      },

      iconClicked (e, icon, row, rowIndex) {
        e.stopPropagation()

        /**
         * При клике на иконку возвращает объект с информацией о строке и на какую иконку был клик. Какие поля входят в объект смотри в properties ==>
         * @param {object} row - объект строки со всеми полями
         * @param {object} icon - объект иконки. Содержит информацию о настройках и поле name с названием иконки
         * @param {number} rowIndex - индекс строки
         */
        this.$emit('iconClicked', { icon, row, rowIndex })
      },

      iconUrl (icon, row) {
        let { type } = icon

        if (typeof type === 'function') {
          type = type(row)
        }

        switch (type) {
          case 'view':
            return require('@/assets/img/document.svg')
          case 'edit':
            return require('@/assets/img/edit-pencil.svg')
          case 'delete':
            return require('@/assets/img/delete-icon.svg')
        }
      },

      sortableClicked (tableColumnIndex) {

        if (this.sortFieldIndex !== tableColumnIndex) {
          this.sortFieldIndex = tableColumnIndex
          this.sortDirection = SortDirection.Down
          return
        }

        if (this.sortDirection === SortDirection.Down) {
          this.sortDirection = SortDirection.Up
          return
        }

        if (this.sortDirection === SortDirection.Up) {
          this.sortDirection = SortDirection.Down
        }
      }
    }
  }
</script>

<style lang="sass" scoped>
.v__table
  width: 100%
  border-collapse: collapse

.v__table th
  color: $black-color
  border: 1px solid #eee
  background-color: #f6f6f6

.v__row
  background-color: white

.v__row td
  border: 1px solid #eee
  color: $black-color

.v__table th,
.v__row td
  padding: 10px
  font-size: 14px
  line-height: 1.2
  font-weight: normal

.v__row.hover__row:hover
  cursor: pointer
  background-color: $white-color-100

.v__row.active__row
  background-color: rgba($primary-color, .1)

.col__right
  text-align: right

.col__center
  text-align: center

.action__icon + .action__icon
  margin-left: 14px

.action__icon
  cursor: pointer
  transition: transform 0.2s

.action__icon:hover
  transform: scale(1.2)

.sortable
  display: flex
  flex-wrap: nowrap
  align-items: center
  justify-content: space-between

.sortable__label,
.sortable__icon
  flex: 0 0 auto

.sortable__icon
  top: 1px
  opacity: 0.4
  position: relative
  &::v-deep svg
    width: 16px
    height: 16px
    fill: rgba(77, 77, 77, .75)

.sortable__label:hover,
.sortable__icon:hover
  cursor: pointer

.sortable__label:hover + .sortable__icon,
.sortable__icon:hover
  opacity: 0.6

.sortable__icon.active
  opacity: 1 !important

.sortable__icon.active.up
  transform: rotate(180deg)
  top: -1px

.bim-table__items-on-page
  display: flex
  align-items: center
</style>
